import { isValidEmail, normalizeEmail } from "../../util/email";
import { FORM_TYPE } from "../../util/applicationLabels";

export { isValidEmail, normalizeEmail };

const EDD_OWNER_EMAIL_FORM_TYPES = new Set([
  FORM_TYPE.IndividualPlus,
  FORM_TYPE.IndividualPrime,
  FORM_TYPE.Business,
  FORM_TYPE.BusinessPlus,
  FORM_TYPE.BusinessPrime,
]);

const normalizeSpecialEmailName = (name) => {
  return typeof name === "string" ? name.trim() : "";
};

export const normalizeSpecialEmail = (specialEmail) => {
  if (!specialEmail || typeof specialEmail !== "object") {
    return null;
  }

  const email = typeof specialEmail.email === "string" ? specialEmail.email.trim() : "";

  if (!email) {
    return null;
  }

  return {
    name: normalizeSpecialEmailName(specialEmail.name),
    email,
  };
};

export const buildSpecialEmailEntry = ({ name, email } = {}) => {
  const normalizedEmail = normalizeEmail(email);

  if (!normalizedEmail) {
    return null;
  }

  return {
    name: normalizeSpecialEmailName(name),
    email: normalizedEmail,
  };
};

export const getSpecialEmails = (applicationFormOrSpecialEmails) => {
  const specialEmails = Array.isArray(applicationFormOrSpecialEmails)
    ? applicationFormOrSpecialEmails
    : applicationFormOrSpecialEmails?.specialEmails;

  if (!Array.isArray(specialEmails)) {
    return [];
  }

  return specialEmails
    .map(normalizeSpecialEmail)
    .filter(Boolean);
};

export const getExistingSpecialEmail = (applicationForm) => {
  return getSpecialEmails(applicationForm)[0] || null;
};

export const getExistingSpecialEmailAddress = (applicationForm) => {
  return getExistingSpecialEmail(applicationForm)?.email || "";
};

export const hasUnsupportedMultipleSpecialEmails = (applicationForm) => {
  return getSpecialEmails(applicationForm).length > 1;
};

export const getPrimaryApplicantEmails = ({ applicationForm } = {}) => {
  const ownerEmail = normalizeEmail(applicationForm?.email);

  return ownerEmail ? [ownerEmail] : [];
};

export const getIndividualTrackEmails = ({ applicationForm, formData } = {}) => {
  const trackSources = [
    applicationForm?.individualTracks,
    formData?.individualTracks,
  ];

  return [...new Set(
    trackSources
      .flatMap((individualTracks) => (Array.isArray(individualTracks) ? individualTracks : []))
      .map((individualTrack) => normalizeEmail(individualTrack?.email))
      .filter(Boolean)
  )];
};

export const isPrimaryApplicant = ({ currentUserEmail, applicationForm } = {}) => {
  const normalizedCurrentUserEmail = normalizeEmail(currentUserEmail);

  if (!normalizedCurrentUserEmail) {
    return false;
  }

  return getPrimaryApplicantEmails({ applicationForm }).includes(normalizedCurrentUserEmail);
};

export const isIndividualTrackApplicant = ({ currentUserEmail, applicationForm, formData } = {}) => {
  const normalizedCurrentUserEmail = normalizeEmail(currentUserEmail);

  if (!normalizedCurrentUserEmail) {
    return false;
  }

  return getIndividualTrackEmails({ applicationForm, formData }).includes(normalizedCurrentUserEmail);
};

export const isEddOwnerEmailApplicant = ({ currentUserEmail, applicationForm } = {}) => {
  if (!EDD_OWNER_EMAIL_FORM_TYPES.has(applicationForm?.formType)) {
    return false;
  }

  return isPrimaryApplicant({ currentUserEmail, applicationForm });
};

export const resolveApplicationFormId = ({ formId, applicationForm, formData } = {}) => {
  return (
    formId ||
    applicationForm?._id ||
    applicationForm?.id ||
    applicationForm?.application_id ||
    formData?.application_id ||
    formData?.form_id ||
    ""
  );
};

export const canInviteAdditionalMember = ({
  currentUserEmail,
  applicationForm,
  formData,
  formId,
  disabled,
} = {}) => {
  if (disabled) {
    return false;
  }

  const resolvedFormId = resolveApplicationFormId({ formId, applicationForm, formData });

  if (!resolvedFormId || !applicationForm) {
    return false;
  }

  return isPrimaryApplicant({ currentUserEmail, applicationForm });
};

export const canInviteAdditionalMemberForEdd = ({
  currentUserEmail,
  applicationForm,
  formData,
  formId,
  disabled,
} = {}) => {
  if (disabled) {
    return false;
  }

  const resolvedFormId = resolveApplicationFormId({ formId, applicationForm, formData });

  if (!resolvedFormId || !applicationForm) {
    return false;
  }

  if (applicationForm?.eDDApplication !== true) {
    return false;
  }

  if (isEddOwnerEmailApplicant({ currentUserEmail, applicationForm })) {
    return true;
  }

  return isIndividualTrackApplicant({ currentUserEmail, applicationForm, formData });
};

export const validateInviteMember = ({
  name,
  email,
  currentUserEmail,
  applicationForm,
  existingSpecialEmail,
} = {}) => {
  const normalizedName = typeof name === "string" ? name.trim() : "";
  const normalizedEmail = normalizeEmail(email);
  const normalizedCurrentUserEmail = normalizeEmail(currentUserEmail);
  const normalizedExistingSpecialEmail = normalizeEmail(
    existingSpecialEmail?.email || existingSpecialEmail || getExistingSpecialEmailAddress(applicationForm)
  );

  if (hasUnsupportedMultipleSpecialEmails(applicationForm)) {
    return "More than one invited member already exists. Remove extra access before inviting a new member.";
  }

  if (normalizedExistingSpecialEmail) {
    return "Remove the existing invited member before inviting another person.";
  }

  if (!normalizedName) {
    return "Name is required.";
  }

  if (!normalizedEmail) {
    return "Email is required.";
  }

  if (!isValidEmail(normalizedEmail)) {
    return "Enter a valid email address.";
  }

  if (normalizedCurrentUserEmail && normalizedCurrentUserEmail === normalizedEmail) {
    return "You cannot invite yourself.";
  }

  return "";
};

export const buildSpecialEmailsPayload = (latestApplicationForm, specialEmails) => {
  return {
    ...(latestApplicationForm || {}),
    specialEmails: Array.isArray(specialEmails)
      ? specialEmails.map(buildSpecialEmailEntry).filter(Boolean)
      : [],
  };
};

export const doSpecialEmailsMatch = (actualSpecialEmails, expectedSpecialEmails) => {
  const actual = getSpecialEmails(actualSpecialEmails).map(buildSpecialEmailEntry);
  const expected = getSpecialEmails(expectedSpecialEmails).map(buildSpecialEmailEntry);

  if (actual.length !== expected.length) {
    return false;
  }

  return actual.every((actualEntry, index) => {
    const expectedEntry = expected[index];
    return (
      actualEntry?.email === expectedEntry?.email &&
      actualEntry?.name === expectedEntry?.name
    );
  });
};

export const mergeApplicationFormUpdate = (previousApplicationForm, updatedApplicationForm) => ({
  ...(previousApplicationForm || {}),
  ...(updatedApplicationForm || {}),
});

export const createApplicationFormUpdateHandler = (setApplicationForm) => (updatedApplicationForm) => {
  if (typeof setApplicationForm !== "function") {
    return;
  }

  return setApplicationForm((previousApplicationForm) => (
    mergeApplicationFormUpdate(previousApplicationForm, updatedApplicationForm)
  ));
};
